import React, { useEffect, useMemo, useRef, useState } from 'react';
import WidgetShell from './WidgetShell';
import { getStrategySheet } from './strategyDataCache';
import { resolveStrategyDisplayName } from './resolveStrategyDisplayName';
import { StrategyCalendarView } from '../StrategyDetailsView';
import {
  buildCalendarPropsFromRawRows,
  makeCurrencyFormatters,
} from '../../utils/strategySheetDerivations';

function CalendarWidget({ config, user, customCombos, editing, onRemove }) {
  const {
    strategy = '',
    contractType = 'NQ',
    contractSize = 1,
  } = config || {};

  const [rawRows, setRawRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const innerRef = useRef(null);
  const [preferCompactNav, setPreferCompactNav] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [calendarRange, setCalendarRange] = useState('month');

  useEffect(() => {
    if (!strategy) {
      setRawRows(null);
      setError('Pick a strategy in the widget settings.');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getStrategySheet({ strategy, email: user?.email })
      .then((data) => {
        if (cancelled) return;
        setRawRows(Array.isArray(data?.rows) ? data.rows : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load strategy data');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [strategy, user?.email, reloadToken]);

  useEffect(() => {
    const body = innerRef.current?.closest('.dashboard-widget-body-calendar');
    if (!body || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setPreferCompactNav(body.clientWidth < 560);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(body);
    return () => ro.disconnect();
  }, [strategy, rawRows]);

  const derived = useMemo(
    () => buildCalendarPropsFromRawRows({ rawRows: rawRows || [], calendarMonth }),
    [rawRows, calendarMonth],
  );

  const { formatCurrencyValue } = useMemo(
    () => makeCurrencyFormatters({ contractType, sheetName: strategy, contractSize: Number(contractSize) || 1 }),
    [contractType, strategy, contractSize],
  );

  const displayStrategyName = resolveStrategyDisplayName(strategy, customCombos);
  return (
    <WidgetShell
      title={`Calendar · ${displayStrategyName || '—'}`}
      subtitle={`${contractType} · ${Number(contractSize) || 1}x default contract size`}
      editing={editing}
      onRemove={onRemove}
      loading={loading}
      error={error}
      onRetry={() => setReloadToken((t) => t + 1)}
      bodyClassName="dashboard-widget-body-calendar"
    >
      {!rawRows || derived.dailyData.size === 0 ? (
        <div className="dashboard-widget-state">No daily data to render in the calendar.</div>
      ) : (
        <div ref={innerRef} className="dashboard-calendar-widget-inner">
          <StrategyCalendarView
            calendarMonth={calendarMonth}
            setCalendarMonth={setCalendarMonth}
            calendarRange={calendarRange}
            setCalendarRange={setCalendarRange}
            dailyData={derived.dailyData}
            monthWeeksSummary={derived.monthWeeksSummary}
            calendarMonthStats={derived.calendarMonthStats}
            yearStats={derived.yearStats}
            formatCurrencyValue={formatCurrencyValue}
            contractType={contractType}
            showRollWeek={false}
            showHighImpactUsd={false}
            highImpactEventsByDate={null}
            sheetName={displayStrategyName}
            preferCompactNav={preferCompactNav}
          />
        </div>
      )}
    </WidgetShell>
  );
}

export default CalendarWidget;
